import { useQuery } from '@tanstack/react-query'
import { api } from '../../utils/api'
import type { FloorPlanning, Placement } from '../../utils/placement.logic'

type FloorRack = Placement & { id: number; label: string }

export function FloorPlanningLayer({ planning, racks, ppm, zoom, onSelect, targetLabel, labels }: {
  planning: FloorPlanning; racks: FloorRack[]; ppm: number; zoom: number;
  onSelect?: (id: number) => void; targetLabel?: (rowId: string, position: number) => string;
  labels?: Record<string, string>;
}) {
  return <g aria-label="Планируемые размещения" data-plan-layer="planning">
    {planning.placements.map(plan => {
      const rack = racks.find(candidate => candidate.id === plan.rack_id)
      if (!rack) return null
      const target = plan.target, width = rack.widthM * ppm, depth = rack.depthM * ppm
      const label = target.row_id && target.rack_pos_in_row && targetLabel
        ? targetLabel(target.row_id, target.rack_pos_in_row) : labels?.[String(rack.id)] ?? (target.row_id ? `Место ${target.rack_pos_in_row}` : 'Вне ряда')
      const x = target.x_m * ppm, y = target.y_m * ppm
      return <g key={plan.rack_id} role="button" tabIndex={0} aria-label={`План переноса: ${rack.label}`} style={{ pointerEvents: 'auto', cursor: 'pointer' }}
        onClick={() => onSelect?.(rack.id)} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect?.(rack.id) } }}>
        <title>{rack.label} → {label}; X {target.x_m} м, Y {target.y_m} м</title>
        <line x1={(rack.xM + rack.widthM / 2) * ppm} y1={(rack.yM + rack.depthM / 2) * ppm}
          x2={x + width / 2} y2={y + depth / 2} stroke="#a3a3a3" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
        <rect x={x} y={y} width={width} height={depth} transform={`rotate(${target.rotation},${x + width / 2},${y + depth / 2})`}
          fill="none" stroke="#fbbf24" strokeWidth={2} strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />
        <text x={x} y={y - 6 / zoom} fontSize={12 / zoom} fill="#fbbf24" paintOrder="stroke" stroke="#111827" strokeWidth={3 / zoom}>План: {label}</text>
      </g>
    })}
  </g>
}

type Reservation = { id: string; kind: 'row_position' | 'rack' | 'u_range'; rack_id?: number; row_id?: string; position?: number; u_start?: number; u_end?: number; label: string; reason?: string }

export function FloorReservationsList({ code, planning }: { code: string; planning: FloorPlanning }) {
  const { data, isError, isLoading } = useQuery<Reservation[]>({ queryKey: ['floor-reservations', code],
    queryFn: () => api.get(`/api/datacenters/${code}/floor-reservations`).then(result => result.data), enabled: !!code,
  })
  const records = (Array.isArray(data) ? data : []).filter(item => item.kind !== 'rack')
  records.push(...planning.rack_reservations.map(item => ({ ...item, kind: 'rack' as const, label: `Стойка #${item.rack_id}` })))
  return <aside aria-label="Резервы зала" style={{ position: 'absolute', right: 12, top: 64, maxWidth: 'calc(100% - 24px)', maxHeight: '35%', overflow: 'auto', padding: 8,
    background: 'var(--bg-panel, #111827)', border: '1px solid var(--border, #374151)', borderRadius: 4, fontSize: 12, zIndex: 5 }}>
    <strong>Резервы</strong>
    {isLoading ? <div role="status">Загрузка резервов...</div> : isError ? <div role="alert">Не удалось загрузить резервы</div> : !records.length && <div>Резервов нет</div>}
    {records.map(item => <div key={`${item.kind}:${item.id}`} style={{ marginTop: 6 }}>
      <a href={item.rack_id ? `/${encodeURIComponent(code)}/racks/${item.rack_id}` : `/${encodeURIComponent(code)}/floor-plan?row=${encodeURIComponent(item.row_id ?? '')}`}>
        {item.kind === 'rack' ? 'Вся стойка' : item.kind === 'u_range' ? `U${item.u_start}–U${item.u_end}` : `Место ${item.position}`} · {item.label}
      </a>{item.reason && <div>{item.reason}</div>}
    </div>)}
  </aside>
}
